import React, { useState } from "react";
import { useUserContext } from "../../context/UserContext.jsx";
import { apiRequest } from "@/network/apiClient";
import { toast } from "react-toastify";
import { API_ENDPOINTS } from "@/api/apiEndpoints";

const InteractionButtons = ({ leadID, onInteractionAdd }) => {
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState({
    notes: "",
    purpose: "",
    date: "",
    type: "",
    leadId: leadID || "", // Ensure it's not null initially
  });

  const purposeOptions = [
    "Promotions/FX Rate",
    "Follow up for document (Order)",
    "Other",
    "Follow up for Next Order (Retention)",
    "Cold Message (Insurance, Financing etc)",
  ];

  const interactionTypes = [
    {
      type: "Call",
      icon: <img src="/icons/call.svg" className="size-7" />,
    },
    {
      type: "Text/Message",
      icon: <img src="/icons/whatsapp.svg" className="size-7" />,
    },
    {
      type: "E-mail",
      icon: <img src="/icons/email.svg" className="size-7" />,
    },
    {
      type: "Events/In-Person",
      icon: <img src="/icons/supervisor_account.svg" className="size-7" />,
    },
  ];

  const handleButtonClick = (type) => {
    setSelectedInteraction(type);
    setIsModalOpen(true);
    setFormData((prevData) => ({
      ...prevData,
      type, // Add the selected type to formData
    }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    // Block future dates even if user types them
    if (name === "date" && value > today) {
      toast.error("Date cannot be in the future.");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault(); // we submit via addInteraction button
    console.log("Form Data:", { ...formData, interactionType: selectedInteraction });
    // No direct submit here; handled by addInteraction
  };

  const addInteraction = async () => {
    // Final guard before calling API
    if (!formData.date) {
      toast.error("Please select a date.");
      return;
    }

    if (formData.date > today) {
      toast.error("Date cannot be in the future.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiRequest(
        `${import.meta.env.VITE_BASE_URL}/${API_ENDPOINTS.ADD_INTERACTION}`,
        "POST",
        formData
      );
      toast.success("Interaction Successfully Added");
      if (onInteractionAdd) {
        onInteractionAdd();
      }
      setIsModalOpen(false);
      // reset fields but keep leadId & type
      setFormData((prev) => ({
        ...prev,
        notes: "",
        purpose: "",
        date: "",
      }));
    } catch (error) {
      toast.error(error || "Error Adding Interaction");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center gap-2 md:gap-3">
        <p className="text-xs md:text-base whitespace-nowrap">
          <b>Add Interactions:</b>
        </p>
        {interactionTypes.map((interaction) => (
          <button
            key={interaction.type}
            onClick={() => handleButtonClick(interaction.type)}
            className="flex items-center bg-white py-1 px-2 border-[1px] border-black justify-center w-9 h-9 md:w-10 md:h-10 rounded-full text-black hover:bg-yellow-200 focus:outline-none flex-shrink-0"
          >
            <img src={interaction.icon.props.src} className="size-5 md:size-7" />
          </button>
        ))}
      </div>

      {/* Custom Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 md:p-6 rounded-lg w-full max-w-md md:w-1/3">
            <div className="border-b border-gray-300 mb-4 md:mb-6 pb-2">
              <h2 className="text-lg md:text-xl font-bold">Add Interaction Details</h2>
            </div>
            {selectedInteraction && (
              <p className="mt-3 md:mt-4 text-sm md:text-base">
                Selected Interaction: <b>{selectedInteraction}</b>
              </p>
            )}
            <form onSubmit={handleSubmit}>
              <div className="mb-3 md:mb-4">
                <label className="block text-sm font-medium mb-1 mt-2 md:mt-3">
                  Purpose:
                </label>
                <select
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleFormChange}
                  className="w-full p-3 border border-gray-300 rounded text-sm md:text-base min-h-[44px]"
                  required
                >
                  <option value="" disabled>
                    Select a purpose
                  </option>
                  {purposeOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3 md:mb-4">
                <label className="block text-sm font-medium mb-1">
                  Date:
                </label>
                {/* disable future dates in picker */}
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleFormChange}
                  className="w-full p-3 border border-gray-300 rounded text-sm md:text-base min-h-[44px]"
                  required
                  max={today}
                />
              </div>
              <div className="mb-3 md:mb-4">
                <label className="block text-sm font-medium mb-1">
                  Notes:
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleFormChange}
                  className="w-full p-3 border border-gray-300 rounded text-sm md:text-base min-h-[88px]"
                  rows={3}
                  required
                />
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-0 sm:space-x-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2.5 md:py-3 bg-white border-[1px] border-black rounded-2xl md:rounded-3xl hover:bg-gray-100 text-sm md:text-base font-bold min-h-[44px]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={addInteraction}
                  className={`rounded-2xl md:rounded-3xl px-6 md:px-8 py-2.5 md:py-3 text-black font-bold text-sm md:text-base min-h-[44px] ${
                    isLoading
                      ? "bg-gray-500"
                      : "bg-yellow-300 hover:bg-yellow-400"
                  }`}
                  disabled={isLoading}
                >
                  {isLoading ? "Adding..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractionButtons;